/**
 * Discogs arm of the create form's Source block.
 *
 * A Discogs recording has no id of its own at the source: DS mints one from
 * the release id and the track's raw position in that release's tracklist
 * (see `utils/discogsTrackId`). So instead of asking the operator to type an
 * id they cannot know, this asks for the release id, fetches the tracklist
 * through the backend's `discogs/release-tracklist/` endpoint, and lets them
 * pick the track or sub-track — the id is derived from the pick and handed to
 * the form as the recording's `sourceId`.
 *
 * The release id lives in the form (it counts towards dirtiness); the fetched
 * tracklist lives here, because nothing else needs it.
 */
import React, { useState } from 'react';
import type { DiscogsReleaseTracklist } from 'shared_components/generated/admin';
import WButton from 'shared_components/components/WForms/WButton/WButton';
import { useApi } from 'shared_components/context';
import { discogsTrackOptionGroups } from '../../../utils/discogsTrackId';
import {
  FIELD_ERROR_CLASS,
  FIELD_HINT_CLASS,
  FIELD_LABEL_CLASS,
  FIELD_LABEL_ROW_CLASS,
  fieldControlClass,
} from './formStyles';
import { isResponseError } from './saveErrorMessage';

export interface DiscogsTrackPickerProps {
  releaseId: string;
  onReleaseIdChange: (releaseId: string) => void;
  /** The derived id, or `''` while no track is picked. */
  sourceId: string;
  onSourceIdChange: (sourceId: string) => void;
}

/**
 * What to tell the operator when the tracklist fetch fails. The backend's
 * 404 means Discogs returned nothing for the id; its 503 means the server has
 * no Discogs key, which must not read as "no such release".
 */
const lookupFailureMessage = (err: unknown): string => {
  if (isResponseError(err)) {
    switch (err.response.status) {
      case 404:
        return 'Release not found on Discogs.';
      case 503:
        return 'Discogs lookup is not configured on the server.';
      default:
        return `Discogs lookup failed (HTTP ${err.response.status}).`;
    }
  }
  return err instanceof Error ? err.message : 'Discogs lookup failed.';
};

export const DiscogsTrackPicker: React.FC<DiscogsTrackPickerProps> = ({
  releaseId,
  onReleaseIdChange,
  sourceId,
  onSourceIdChange,
}) => {
  const { adminApi: api } = useApi();
  const [tracklist, setTracklist] = useState<DiscogsReleaseTracklist | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setTracklist(null);
    setError(null);
    if (sourceId) onSourceIdChange('');
  };

  const handleReleaseIdChange = (next: string) => {
    onReleaseIdChange(next);
    // A different release means a different tracklist and a stale id.
    reset();
  };

  const fetchTracklist = async () => {
    const id = releaseId.trim();
    if (!id || loading) return;
    setLoading(true);
    reset();
    try {
      const result = await api.releaseTracklistDiscogsReleaseTracklist({
        releaseId: id,
      });
      setTracklist(result);
    } catch (err: unknown) {
      setError(lookupFailureMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const groups = tracklist ? discogsTrackOptionGroups(tracklist) : [];

  return (
    <div className="tw-flex tw-flex-col tw-gap-4 tw-w-full">
      <div className="tw-flex-1 tw-min-w-0">
        <div className={FIELD_LABEL_ROW_CLASS}>
          {/* Not tagged Optional: the source recording id is derived from
              the pick, and that id is required for a Discogs recording. */}
          <label htmlFor="modal-discogs-release-id" className={FIELD_LABEL_CLASS}>
            Discogs Release ID
          </label>
        </div>
        <div className="tw-flex tw-gap-2 tw-items-center">
          <input
            id="modal-discogs-release-id"
            type="text"
            value={releaseId}
            onChange={(e) => handleReleaseIdChange(e.target.value)}
            className={fieldControlClass({ className: 'tw-flex-1' })}
            placeholder="The release the recording appears on"
          />
          <WButton
            variant="secondary"
            label={loading ? 'Fetching…' : 'Fetch tracks'}
            disabled={loading || !releaseId.trim()}
            onClick={fetchTracklist}
          />
        </div>
        {error && <p className={`${FIELD_ERROR_CLASS} tw-mt-2`}>{error}</p>}
      </div>

      {tracklist && (
        <div className="tw-flex-1 tw-min-w-0">
          <label
            htmlFor="modal-discogs-track"
            className={`${FIELD_LABEL_CLASS} tw-mb-1`}
          >
            Track
          </label>
          {/* Under the label, not beside it: classical box sets carry
              artist-and-title lines long enough to wrap the label row. */}
          <p className={`${FIELD_HINT_CLASS} tw-mb-2`}>
            {[tracklist.artist, tracklist.title].filter(Boolean).join(' – ')}
          </p>
          {groups.length === 0 ? (
            <p className={FIELD_HINT_CLASS}>
              This release lists no tracks to pick from.
            </p>
          ) : (
            <select
              id="modal-discogs-track"
              value={sourceId}
              onChange={(e) => onSourceIdChange(e.target.value)}
              className={fieldControlClass({
                kind: 'select',
                hasValue: !!sourceId,
              })}
            >
              <option value="">Select track</option>
              {groups.map((group, i) =>
                group.label === null ? (
                  group.options.map((option) => (
                    <option key={option.sourceId} value={option.sourceId}>
                      {option.label}
                    </option>
                  ))
                ) : (
                  <optgroup key={`${group.label}-${i}`} label={group.label}>
                    {group.options.map((option) => (
                      <option key={option.sourceId} value={option.sourceId}>
                        {option.label}
                      </option>
                    ))}
                  </optgroup>
                )
              )}
            </select>
          )}
        </div>
      )}
    </div>
  );
};
